import React, { forwardRef, useRef } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../Icon';
import DeleteIcon from '../../../public/static/images/new_search/icons/icon-delete.svg';
import { useCombinedRefs } from '../../../hooks/useCombinedRefs';
import {
  NumberFieldContainer,
  StyledDeleteIcon,
  StyledNumberField,
  StyledNumberInput,
  StyledTextLabel
} from './styled';
import { useHideCaretUnderHeader } from './hooks/useHideCaretUnderHeader';
import { NumberComponentProps, NumberFieldProps } from './types';

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  (
    {
      name,
      placeholder,
      id,
      stat,
      value = '',
      onChange,
      onReset,
      isFullWidth,
      floatingPlaceholder,
      onFocusHandler,
      onBlurHandler,
      onPasteHandler,
      containerRef,
      headerRef,
      isCustomInput = false
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { change } = useForm();
    const { values } = useFormState({
      subscription: { values: true }
    });
    const inputRef = useRef<HTMLInputElement | null>(null);
    const combinedRef = useCombinedRefs(ref, inputRef);

    const resetValue = () => {
      change(name);
      if (onReset) {
        onReset();
      }
    };

    const noLeadingZeroNumber = (val = '') => {
      if (typeof val === 'string' && val.startsWith('0') && val.length > 1) {
        return val.replace(/^0/, '').replace(/\D/g, '');
      }

      return val;
    };

    const onlyNums = (val = '') =>
      typeof val === 'string' ? val.replace(/\D/g, '') : val;

    const isFocused = document.activeElement === combinedRef.current;

    const { resetInputCaretStyle } = useHideCaretUnderHeader(
      headerRef,
      inputRef,
      isFocused,
      containerRef
    );

    const focusInput = () => {
      combinedRef.current?.focus();
      resetInputCaretStyle();
    };

    const Component = (
      isCustomInput ? StyledNumberInput : StyledNumberField
    ) as React.ForwardRefExoticComponent<NumberComponentProps>;

    return (
      <NumberFieldContainer $isFullWidth={isFullWidth}>
        {floatingPlaceholder && (
          <StyledTextLabel
            $isNumber={true}
            role="presentation"
            onClick={focusInput}
            $isActive={isFocused || !!value}
          >
            {floatingPlaceholder}
          </StyledTextLabel>
        )}

        <Component
          ref={combinedRef}
          id={id}
          name={name}
          data-stat={stat}
          pattern="[0-9]*"
          component="input"
          autoComplete="off"
          inputMode="numeric"
          maxLength={9}
          parse={onlyNums}
          format={isCustomInput ? null : noLeadingZeroNumber}
          placeholder={placeholder}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onPaste={onPasteHandler}
          {...(onChange && {
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              onChange(noLeadingZeroNumber(onlyNums(e.target.value)));
            },
            initialValue: value,
            value
          })}
        />
        <StyledDeleteIcon
          tabIndex={0}
          role="button"
          onClick={resetValue}
          onKeyPress={resetValue}
          $isVisible={!!(values[name] || value)}
          data-cy="filters__delete-icon"
          aria-label={t('common.reset')}
        >
          <Icon width={15} height={15} IconComponent={DeleteIcon} />
        </StyledDeleteIcon>
      </NumberFieldContainer>
    );
  }
);

NumberField.displayName = 'NumberField';
